import { createSelector } from '../lib/createSelector';
import { activeCompany, companyId, user } from './userSelectors';
import { query } from './querySelectors.js';
import { param } from './uiSelectors.js';
import { isApiIntegration } from '@/lib/isApiIntegration.js';

const EMPTY_INTEGRATIONS = Object.freeze([]);
const EMPTY_AUTH_FIELDS = Object.freeze([]);

export const embeddedIntegrations = (state) => (state.embedded.companyId === companyId(state) ? state.embedded.integrations : EMPTY_INTEGRATIONS);

// The embedded MCP route accepts only boolean true, so the dashboard reads the
// company switch with the same rule.
export const embeddedMcpServerEnabled = (state) => activeCompany(state)?.settings?.enableEmbeddedMcp === true;

export const embeddedApiCatalogEnabled = (state) => activeCompany(state)?.settings?.enableEmbeddedApiCatalog === true;

export const embeddedCanEditAppAccess = createSelector('embeddedCanEditAppAccess', [user, companyId], (user, companyId) => {
  return Boolean(companyId && user?.jwt?.authorizationCompanyId === companyId && user?.jwt?.permissions?.includes('company:edit'));
});

export const embeddedCanEditIntegrations = createSelector('embeddedCanEditIntegrations', [user, companyId], (user, companyId) => {
  return Boolean(companyId && user?.jwt?.authorizationCompanyId === companyId && user?.jwt?.permissions?.includes('embedded:edit'));
});

export const embeddedCanCreateIntegrations = createSelector('embeddedCanCreateIntegrations', [user, companyId], (user, companyId) => {
  return Boolean(companyId && user?.jwt?.authorizationCompanyId === companyId && user?.jwt?.permissions?.includes('embedded:create'));
});

export const embeddedHasFetchedMetadata = (state) => state.embedded.companyId === companyId(state) && state.embedded.hasFetchedMetadata;
export const embeddedMetadataLoading = (state) => state.embedded.companyId !== companyId(state) || !state.embedded.hasFetchedMetadata || state.embedded.metadataRequestId !== null;
export const embeddedMetadataFailed = (state) => state.embedded.companyId === companyId(state) && state.embedded.metadataFailed;
export const embeddedIntegrationFailed = (state, integrationId) => state.embedded.companyId === companyId(state) && state.embedded.integrationFailures?.[integrationId] === true;
export const embeddedIntegrationHasDetail = (state, integrationId) => state.embedded.companyId === companyId(state) && state.embedded.integrationDetailsLoaded?.[integrationId] === true;
export const embeddedIntegrationLoading = (state, integrationId) => state.embedded.companyId === companyId(state) && state.embedded.integrationRequestIds?.[integrationId] != null;

export const embeddedIntegrationById = createSelector('embeddedIntegrationById', [embeddedIntegrations, (_, integrationId) => integrationId], (embeddedIntegrations, integrationId) => {
  return embeddedIntegrations?.find((n) => n?.id === integrationId);
});

export const embeddedActiveSection = createSelector('embeddedActiveSection', [(state) => param(state, 'integrationId'), (state) => query(state, 'type'), embeddedIntegrations], (integrationId, requestedType, integrations) => {
  if (!integrationId) {
    return 'home';
  }
  if (integrationId === 'sdk' || integrationId === 'workflows' || integrationId === 'credential-exchange') {
    return 'sdk';
  }
  if (integrationId === 'mcp') {
    return 'mcp';
  }
  if (integrationId === 'integrations' || integrationId === 'api') {
    return requestedType === 'workflows' ? 'sdk' : 'api';
  }
  if (integrationId === 'connections') {
    return 'api';
  }
  const integration = integrations?.find((item) => item.id === integrationId);
  if (!integration) {
    return null;
  }
  return isApiIntegration(integration) ? 'api' : 'sdk';
});

export const integrationWorkflowsByIntegrationId = createSelector('integrationWorkflowsByIntegrationId', [embeddedIntegrationById], (integration) => {
  return integration?.workflows || [];
});

export const integrationAppsByIntegrationId = createSelector('integrationAppsByIntegrationId', [embeddedIntegrationById], (integration) => {
  return integration?.apps || [];
});

export const integrationNameById = createSelector('integrationNameById', [embeddedIntegrationById], (integration) => {
  return integration?.name || '';
});

export const provisionedCredential = (state) => (state.embedded.companyId === companyId(state) ? state.embedded.provisionedCredential : null);
export const credentialExchangeCustomAuthFields = (state) => (state.embedded.companyId === companyId(state) ? state.embedded.credentialExchangeCustomAuthFields : EMPTY_AUTH_FIELDS);
